/**
 * Connect Token Verification Service
 *
 * Used by customer portal to verify Connect tokens received from MSP portal.
 *
 * Provides:
 * - Token signature verification using public keys
 * - Token freshness validation
 * - JWKS-style public key management
 *
 * Usage in customer portal:
 * const verifier = new ConnectTokenVerifier(jwksUrl);
 * const isValid = await verifier.verifyToken(token, params);
 */

export interface PublicKeyInfo {
  kid: string;
  publicKeyPem: string;
  algorithm: string;
  use: string;
  createdAt: Date;
}

export interface VerificationResult {
  valid: boolean;
  kid?: string;
  verified?: boolean;
  message: string;
  verifiedAt?: Date;
}

export interface ConnectTokenPayload {
  mspTenantId: string;
  customerTenantId: string;
  b2bUpn: string;
  profileId: string;
  targetUrl: string;
  issuedAtUtc: string;
}

/**
 * Verifies Connect tokens using cached public keys
 *
 * The verification process:
 * 1. Extract KID from token
 * 2. Fetch public key for KID (cached)
 * 3. Verify signature using SHA256/RSA PKCS#1
 * 4. Validate token freshness
 * 5. Validate canonical payload
 */
class ConnectTokenVerifier {
  private publicKeyCache: Map<string, PublicKeyInfo> = new Map();
  private jwksUrl?: string;
  private readonly CACHE_TTL_MS = 3600000; // 1 hour
  private readonly TOKEN_MAX_AGE_SECONDS = 300; // 5 minutes

  constructor(jwksUrl?: string) {
    this.jwksUrl = jwksUrl;
  }

  /**
   * Verify a Connect token
   *
   * @param token Connect token from URL parameters
   * @param payload Original payload that was signed
   * @returns Verification result
   */
  public async verifyToken(
    token: { kid: string; signature: string; issuedAtUtc: string },
    payload: ConnectTokenPayload
  ): Promise<VerificationResult> {
    try {
      // Step 1: Validate token is fresh
      if (!this.isTokenFresh(token.issuedAtUtc)) {
        return {
          valid: false,
          kid: token.kid,
          message: `Token expired. Max age: ${this.TOKEN_MAX_AGE_SECONDS} seconds`,
        };
      }

      // Step 2: Get public key for this KID
      const publicKey = await this.getPublicKey(token.kid);
      if (!publicKey) {
        return {
          valid: false,
          kid: token.kid,
          message: `Public key not found for KID: ${token.kid}`,
        };
      }

      // Step 3: Build canonical payload (must match server)
      const canonicalPayload = this.buildCanonicalPayload(payload);

      // Step 4: Verify signature
      const isValid = await this.verifySignature(
        canonicalPayload,
        token.signature,
        publicKey.publicKeyPem
      );

      if (!isValid) {
        return {
          valid: false,
          kid: token.kid,
          message: 'Signature verification failed',
        };
      }

      return {
        valid: true,
        kid: token.kid,
        verified: true,
        message: 'Token verified successfully',
        verifiedAt: new Date(),
      };
    } catch (error) {
      console.error('Token verification error:', error);
      return {
        valid: false,
        message: `Verification error: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }

  /**
   * Get public key for a KID
   *
   * Uses cache first, then fetches from JWKS endpoint if needed
   */
  private async getPublicKey(kid: string): Promise<PublicKeyInfo | null> {
    // Check cache first
    const cached = this.publicKeyCache.get(kid);
    if (cached && !this.isCacheExpired(cached.createdAt)) {
      console.debug(`Public key cache hit for KID: ${kid}`);
      return cached;
    }

    // Fetch from JWKS endpoint
    if (!this.jwksUrl) {
      console.warn('JWKS URL not configured, cannot fetch public keys');
      return null;
    }

    try {
      const response = await fetch(this.jwksUrl);
      if (!response.ok) {
        console.error(`Failed to fetch JWKS: HTTP ${response.status}`);
        return null;
      }

      const jwks = await response.json();
      const keyInfo = jwks.keys?.find((k: any) => k.kid === kid);

      if (!keyInfo) {
        console.warn(`KID not found in JWKS: ${kid}`);
        return null;
      }

      // Parse the public key
      const publicKeyPem = this.extractPublicKeyPem(keyInfo);
      if (!publicKeyPem) {
        return null;
      }

      const publicKey: PublicKeyInfo = {
        kid,
        publicKeyPem,
        algorithm: keyInfo.alg || 'RS256',
        use: keyInfo.use || 'sig',
        createdAt: new Date(),
      };

      // Cache it
      this.publicKeyCache.set(kid, publicKey);
      return publicKey;
    } catch (error) {
      console.error('Failed to fetch public key:', error);
      return null;
    }
  }

  /**
   * Verify signature using Web Crypto API
   *
   * Uses SHA-256 with RSA PKCS#1 v1.5 padding
   */
  private async verifySignature(
    payload: string,
    signature: string,
    publicKeyPem: string
  ): Promise<boolean> {
    try {
      // Decode base64url signature to bytes
      const signatureBytes = this.base64urlToBytes(signature);

      // Create CryptoKey from PEM
      const cryptoKey = await this.pemToPublicKey(publicKeyPem);

      // Verify signature
      const textEncoder = new TextEncoder();
      const data = textEncoder.encode(payload);

      const isValid = await crypto.subtle.verify(
        {
          name: 'RSASSA-PKCS1-v1_5',
          hash: { name: 'SHA-256' },
        },
        cryptoKey,
        signatureBytes,
        data
      );

      return isValid;
    } catch (error) {
      console.error('Signature verification error:', error);
      return false;
    }
  }

  /**
   * Build canonical payload for verification
   * Must match exactly what the server signed
   */
  private buildCanonicalPayload(payload: ConnectTokenPayload): string {
    return [
      payload.mspTenantId.trim(),
      payload.customerTenantId.trim(),
      payload.b2bUpn.trim().toLowerCase(),
      payload.profileId.trim(),
      payload.targetUrl.trim(),
      payload.issuedAtUtc.trim(),
    ].join('\n');
  }

  /**
   * Check if token is fresh (not expired)
   */
  private isTokenFresh(issuedAtUtc: string): boolean {
    try {
      const issuedAt = new Date(issuedAtUtc);
      const now = new Date();
      const ageSeconds = (now.getTime() - issuedAt.getTime()) / 1000;
      return ageSeconds >= 0 && ageSeconds <= this.TOKEN_MAX_AGE_SECONDS;
    } catch {
      return false;
    }
  }

  /**
   * Check if cache entry is expired
   */
  private isCacheExpired(createdAt: Date): boolean {
    return Date.now() - createdAt.getTime() > this.CACHE_TTL_MS;
  }

  /**
   * Convert PEM public key to Web Crypto API CryptoKey
   */
  private async pemToPublicKey(pemString: string): Promise<CryptoKey> {
    // Remove PEM headers
    const base64 = pemString
      .replace(/-----BEGIN PUBLIC KEY-----/g, '')
      .replace(/-----END PUBLIC KEY-----/g, '')
      .replace(/\s+/g, '');

    // Convert to ArrayBuffer
    const binaryString = atob(base64);
    const ab = new ArrayBuffer(binaryString.length);
    const bytes = new Uint8Array(ab);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Import as CryptoKey
    return crypto.subtle.importKey(
      'spki',
      ab,
      {
        name: 'RSASSA-PKCS1-v1_5',
        hash: { name: 'SHA-256' },
      },
      false,
      ['verify']
    );
  }

  /**
   * Convert base64url string to Uint8Array
   */
  private base64urlToBytes(base64url: string): Uint8Array<ArrayBuffer> {
    // Add padding if needed
    const base64 = base64url
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

    const binaryString = atob(padded);
    const ab = new ArrayBuffer(binaryString.length);
    const bytes = new Uint8Array(ab);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Extract public key PEM from JWKS key info
   * Handles both PEM format and JWK format
   */
  private extractPublicKeyPem(keyInfo: any): string | null {
    // If PEM is directly provided
    if (keyInfo.pem || keyInfo.public_key) {
      return keyInfo.pem || keyInfo.public_key;
    }

    // If JWK format, convert to PEM (simplified for RSA)
    if (keyInfo.kty === 'RSA' && keyInfo.n && keyInfo.e) {
      console.warn(
        'JWKS in JWK format detected. Conversion not yet implemented.'
      );
      return null;
    }

    return null;
  }

  /**
   * Parse Connect token and launch parameters from URL search params.
   */
  public parseConnectTokenFromUrl(
    searchParams: URLSearchParams
  ): {
    kid: string;
    signature: string;
    issuedAtUtc: string;
    mspTenantId?: string;
    customerTenantId?: string;
    profileId?: string;
    b2bUpn?: string;
    targetUrl?: string;
  } | null {
    const kid = searchParams.get('kid');
    const signature = searchParams.get('signature');
    const issuedAtUtc = searchParams.get('issuedAtUtc');

    if (!kid || !signature || !issuedAtUtc) {
      console.warn('Missing required Connect token parameters in URL');
      return null;
    }

    return {
      kid,
      signature,
      issuedAtUtc,
      mspTenantId: searchParams.get('mspTenantId') ?? undefined,
      customerTenantId: searchParams.get('customerTenantId') ?? undefined,
      profileId: searchParams.get('profileId') ?? undefined,
      b2bUpn: searchParams.get('b2bUpn') ?? undefined,
      targetUrl: searchParams.get('targetUrl') ?? undefined,
    };
  }

  /**
   * Clear the public key cache
   */
  public clearCache(): void {
    this.publicKeyCache.clear();
    console.debug('Public key cache cleared');
  }

  /**
   * Get cache statistics for debugging
   */
  public getCacheStats(): object {
    return {
      cachedKeys: this.publicKeyCache.size,
      keys: Array.from(this.publicKeyCache.keys()),
    };
  }
}

// Export singleton instance for customer portal
export const connectTokenVerifier = new ConnectTokenVerifier();

// Export class for testing/advanced usage
export default ConnectTokenVerifier;
